import { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { GET_WARDROBE_ITEMS_QUERY } from "@/graphql/queries/wardrobe";
import Breadcrumb from "@/components/Breadcrumb";
import OutfitItemCard from "@/components/outfits/OutfitItemCard";

import { useLocation, useNavigate } from "react-router-dom";

export default function WardrobePage() {
  const { data, loading, error, refetch } = useQuery(GET_WARDROBE_ITEMS_QUERY);
  const location = useLocation();
  const navigate = useNavigate();

  // Refetch on redirect after upload
  useEffect(() => {
    if (location.state && (location.state as any).refetch) {
      refetch();
      // Remove refetch state so it doesn't trigger again
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, refetch, navigate]);
  const outfitItems = data?.getCurrentUser?.outfitItems || [];
  const [search, setSearch] = useState("");
  const [styleFilter, setStyleFilter] = useState("");

  // Compute filtered outfits
  const filteredOutfitItems = outfitItems.filter((outfitItem: any) => {
    const matchesSearch =
      !search || outfitItem.style.toLowerCase().includes(search.toLowerCase());
    const matchesStyle = !styleFilter || outfitItem.style === styleFilter;
    return matchesSearch && matchesStyle;
  });

  // Collect unique styles for filtering
  const styles = Array.from(
    new Set(outfitItems.map((o: any) => o.style).filter(Boolean))
  );

  return (
    <>
      {/* Mobile-friendly header with stacked layout on small screens */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
        <Breadcrumb
          title="Wardrobe"
          trail={[{ label: "Dashboard", to: "/" }, { label: "Wardrobe" }]}
          isMobile={true}
        />
        
        {/* Search and filter controls - full width on mobile, normal on desktop */}
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <div className="relative flex-grow sm:flex-grow-0">
            <input
              type="text"
              placeholder="Search items..."
              className="w-full sm:w-auto border rounded px-3 py-2 focus:outline-none focus:ring focus:border-indigo-400 dark:bg-gray-800 dark:text-white"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 180 }}
            />
          </div>
          <select
            className="w-full sm:w-auto border rounded px-3 py-2 pr-8 focus:outline-none focus:ring focus:border-indigo-400 dark:bg-gray-800 dark:text-white"
            value={styleFilter}
            onChange={(e) => setStyleFilter(e.target.value)}
          >
            <option value="">All Styles</option>
            {(styles as string[]).map((style) => (
              <option key={style} value={style}>
                {style}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Added extra spacing on mobile between filters and items */}
      <section className="mt-6 sm:mt-4 mb-8">
        <div className="max-w-7xl mx-auto">
          {/* Outfits Grid */}
          {loading ? (
            <div className="text-gray-500 dark:text-gray-400">
              Loading items...
            </div>
          ) : error ? (
            <div className="text-red-500">
              Error loading items. {error.message}
            </div>
          ) : filteredOutfitItems.length === 0 ? (
            <div className="text-gray-500 dark:text-gray-400">
              No items found.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {filteredOutfitItems.map((outfitItem: any) => (
                <OutfitItemCard
                  key={outfitItem.id}
                  id={outfitItem.id}
                  style={outfitItem.style}
                  category={outfitItem.category?.name || ""}
                  colors={outfitItem.colors}
                  imageUrl={outfitItem.imageUrl}
                  description={outfitItem.description}
                  onClick={() => navigate(`/wardrobe/item/${outfitItem.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
